import React, { FC, useEffect, useState } from 'react';
import {
  CloseModalButton,
  FormWrapper,
  Header,
  InnerScroller,
  LeftSideBarWrapper,
  MobileHeading,
  Modal,
  PrevStepButton,
  RightSideBarWrapper,
  SplitBarWrapper,
  Title,
} from '@common/components/BookingForms';
import { Spinner, StickyBarContent, StickyBarWrapper } from '@common/components';
import { SignIn } from '@features/SignIn';
import { SpaPackageType } from 'shared-types/SpaPackage';
import { Property } from 'shared-types/Property';
import { Price } from 'shared-types/Common';
import { useRouter } from 'shared-routing';
import { DispatchProps, StateProps } from './SpaPackageModal.types';
import { getCurrentStep } from './bookingSteps';
import { SPA_PACKAGE_FORM_NAME } from './shared';
import { FormattedLink } from '@common/components/FormattedLink';
import { usePageContext } from '@common/components/PageContext';
import { SubHeader } from './SpaPackageModal.styles';
import { useGetSpaPackageTimeslots } from '@features/spa/hooks/useGetSpaPackageTimeslots';
import { useGetSpaPackageExtras } from '@features/spa/hooks/useGetSpaPackageExtras';
import { useGetSpaPackage } from '@features/spa';

type SpaPackageModalProps = {
  entity: SpaPackageType;
  bookingStep: string;
  language: string;
  property: Property;
  handleCloseModal: (arg: boolean) => void;
  startDate: string;
} & StateProps &
  DispatchProps;

const calculatePrice = (entityPrice: Price, entityCount: number, extrasTotal: number): Price => ({
  ...entityPrice,
  amount: entityPrice.amount * entityCount + extrasTotal,
});

export const SpaPackageModal: FC<SpaPackageModalProps> = ({
  arrivalTime,
  bookingStep,
  entity,
  property,
  language,
  isFetchingAvailability,
  extrasTotal,
  guestsCount,
  selectedDate,
  startDate,
  destroy,
  handleCloseModal,
}) => {
  const router = useRouter();
  useEffect(
    () => () => {
      destroy(SPA_PACKAGE_FORM_NAME);
    },
    []
  );

  const [unavailable, setUnavailable] = useState(true);
  const { name, id: spaDayId, telephoneOnly } = entity;
  const selectedDateString = selectedDate.format('YYYY-MM-DD');

  const { data: spaPackage, isFetching: isFetchingSpaPackage } = useGetSpaPackage(
    property.id,
    spaDayId,
    language,
    false,
    selectedDateString
  );

  const { data: extras = [] } = useGetSpaPackageExtras(
    property.id,
    spaDayId,
    language,
    selectedDateString
  );
  const { form, goToPreviousStep, stickyButton, titleId } = getCurrentStep({
    arrivalTime,
    unavailable,
    bookingStep,
    entity,
    extras,
    property,
    spaDayId,
    language,
    setUnavailable,
    handleCloseModal,
    router,
    maxGuests: spaPackage?.maximumGuestsPerBasket || Infinity,
  });
  const { data: timeslots } = useGetSpaPackageTimeslots(
    property.id,
    entity.id,
    startDate,
    entity.numberOfGuests,
    7,
    !unavailable
  );

  const selectedDateTimeSlots = timeslots?.[selectedDateString];

  const discount = selectedDateTimeSlots?.find(
    (slot) => slot.startTime === arrivalTime?.startTime
  )?.discount;

  const price = selectedDateTimeSlots?.find(
    (slot) => slot.startTime === arrivalTime?.startTime
  )?.price;

  const { routingData } = usePageContext();

  if (!bookingStep || form === null) {
    return null;
  }
  return (
    <Modal isVisible>
      <>
        <Spinner showDelayMessage isActive={isFetchingAvailability || isFetchingSpaPackage} />
        <Header>
          <PrevStepButton onClick={goToPreviousStep} />
          <MobileHeading>{name}</MobileHeading>
          <CloseModalButton onClick={() => handleCloseModal(false)} />
        </Header>
        <InnerScroller scrollToTopIndicator={bookingStep}>
          <FormWrapper>
            <SubHeader>
              {routingData && (
                <FormattedLink format="button" href={routingData.href}>
                  {routingData.message}
                </FormattedLink>
              )}
            </SubHeader>
            <Title titleId={titleId} />
            <SplitBarWrapper>
              <LeftSideBarWrapper>{form}</LeftSideBarWrapper>
              <RightSideBarWrapper>
                <SignIn />
              </RightSideBarWrapper>
            </SplitBarWrapper>
          </FormWrapper>
        </InnerScroller>
        {!telephoneOnly && (
          <StickyBarWrapper>
            <StickyBarContent
              name={entity.name}
              displayedPrice={calculatePrice(price || entity.price, guestsCount, extrasTotal)}
              discountedPrice={
                discount
                  ? {
                      ...discount,
                      discountedPrice: calculatePrice(
                        discount.discountedPrice,
                        guestsCount,
                        extrasTotal
                      ),
                    }
                  : undefined
              }
              button={stickyButton}
            />
          </StickyBarWrapper>
        )}
      </>
    </Modal>
  );
};
